import React, { useEffect, useState } from "react"
import { ArrowRightLeft, Loader2 } from "lucide-react"

import { Button } from "src/components/ui/button"
import { Label } from "src/components/ui/label"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { fetchRouting, updateRouting } from "src/features/integrations/api/providersApi"

const CHANNEL_LABELS = { whatsapp: "WhatsApp", sms: "SMS", call: "Calls" }

// Per-channel provider routing: which provider carries WhatsApp, SMS and calls.
// The backend only offers providers that can actually handle each channel.
export default function RoutingCard() {
  const { toast } = useToast()
  const [routing, setRouting] = useState(null)
  const [options, setOptions] = useState({})
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    fetchRouting()
      .then((d) => { setRouting(d.routing); setOptions(d.options || {}) })
      .catch(() => setRouting({}))
  }, [])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const d = await updateRouting(routing)
      setRouting(d.routing)
      setOptions(d.options || {})
      toast({ title: "Channel routing saved" })
    } catch (e) {
      toast({
        variant: "destructive", title: "Couldn't save routing",
        description: e?.response?.data?.error || "Something went wrong",
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-500/10 to-slate-600/5 p-6 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-200/50">
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 items-center justify-center">
          <ArrowRightLeft color="#475569" size={26} />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-900">Channel routing</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            Choose which provider carries each channel. Only providers that support a channel are offered.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {routing === null ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading routing…
          </div>
        ) : (
          <>
            {Object.keys(CHANNEL_LABELS).map((channel) => (
              <div key={channel} className="grid gap-1.5">
                <Label className="text-xs font-medium text-slate-600">{CHANNEL_LABELS[channel]}</Label>
                <Select
                  value={routing[channel] || ""}
                  onValueChange={(v) => setRouting((r) => ({ ...r, [channel]: v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select a provider" /></SelectTrigger>
                  <SelectContent>
                    {(options[channel] || []).map((opt) => (
                      <SelectItem key={opt.key} value={opt.key}>
                        {opt.name}{opt.configured ? "" : " (not configured)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}

            <div className="flex items-center justify-end">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save routing
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
